/**
 * UDA boundaries for every WMS map, from the one place that holds them:
 * `ds_dtcp.ds_uda_lst_t.extd_bndry_plgn_jn` — a GeoJSON MultiPolygon per authority, served by
 * `ds-wms/dashboard/uda-boundaries`.
 *
 * Before this, only the integrated dashboard drew real UDA geometry. Every other WMS map
 * opened on a hardcoded town centroid (see UDA_CENTROIDS in udaGeo.ts), so a proposal
 * drawn for Eluru opened on a point with no authority outline around it and nothing to
 * say whether the shape being drawn fell inside the UDA at all.
 *
 * Two UDAs — APCRDA (11) and VK-PCPIR (6) — carry no polygon in the master. They come
 * back in `withoutGeometry`, and callers fall back to the centroid view rather than
 * showing an empty map.
 *
 * The request is scoped by the WMS UDA header like every other ds-wms call, so a
 * restricted login receives only its own geometry; the cache is therefore keyed by the
 * scope in force.
 */
import type L from 'leaflet';
import axiosInstance from '@/services/interceptor';
import { useWmsUdaScopeStore } from '../components/common/WmsUdaScope';

export interface UdaBoundaryProps {
  udaId: number;
  udaCode?: string;
  udaName?: string;
  worksCount?: number;
  totalValue?: number;
  physicalProgress?: number;
}

export interface UdaBoundaryFeature {
  type: 'Feature';
  id?: number;
  geometry: { type: string; coordinates: any };
  properties: UdaBoundaryProps & Record<string, any>;
}

export interface UdaBoundaryCollection {
  type: 'FeatureCollection';
  features: UdaBoundaryFeature[];
  /** UDAs the master holds no polygon for — named so a blank map can say why. */
  withoutGeometry: Array<{ udaId: number; udaName?: string }>;
}

const EMPTY: UdaBoundaryCollection = { type: 'FeatureCollection', features: [], withoutGeometry: [] };

/** Keyed by the scope in force: 'all' or the uda id list the header will carry. */
const cache = new Map<string, Promise<UdaBoundaryCollection>>();

const scopeKey = (): string => {
  const udaId = useWmsUdaScopeStore.getState().udaId;
  return udaId == null ? 'all' : String(udaId);
};

/**
 * The UDA polygons the caller is allowed to see. Cached per scope for the session —
 * the geometry is master data and several maps ask for it on the same screen.
 */
export async function fetchUdaBoundaries(): Promise<UdaBoundaryCollection> {
  const key = scopeKey();
  const hit = cache.get(key);
  if (hit) return hit;

  const req = (async () => {
    const resp = await axiosInstance.get('ds-wms/dashboard/uda-boundaries');
    const payload = resp?.data?.data;
    if (!payload?.features?.length) {
      // Not cached as a failure: a later caller (after the scope changes, or once the
      // request that failed succeeds) should be able to try again.
      cache.delete(key);
      return { ...EMPTY, withoutGeometry: payload?.withoutGeometry || [] };
    }
    return {
      type: 'FeatureCollection',
      features: payload.features as UdaBoundaryFeature[],
      withoutGeometry: payload.withoutGeometry || [],
    } as UdaBoundaryCollection;
  })().catch((err) => {
    console.warn('[ds-wms] UDA boundaries request failed — maps will fall back to centroids.', err);
    cache.delete(key);
    return EMPTY;
  });

  cache.set(key, req);
  return req;
}

/** One authority's polygon, or null when the master holds none for it. */
export async function fetchUdaBoundary(udaId?: number | null): Promise<UdaBoundaryFeature | null> {
  if (udaId == null) return null;
  const fc = await fetchUdaBoundaries();
  return fc.features.find((f) => Number(f.properties?.udaId) === Number(udaId)) || null;
}

export interface DrawUdaBoundaryOptions {
  /** Outline colour. Defaults to the WMS violet used by the dashboard's UDA layer. */
  color?: string;
  /** Frame the map on the polygon once it is drawn. Default true. */
  fit?: boolean;
  /** Label shown on hover. Defaults to the UDA name. */
  tooltip?: string | false;
}

/**
 * Draw one UDA outline on a Leaflet map as CONTEXT, not as a target: it takes no clicks
 * and no pointer events, so it can never sit between the operator and the marker or
 * polygon they are placing. Returns the layer so the caller can remove it.
 */
export function drawUdaBoundary(
  Lmod: typeof L,
  map: L.Map,
  feature: UdaBoundaryFeature,
  opts: DrawUdaBoundaryOptions = {}
): L.GeoJSON {
  const { color = '#7c3aed', fit = true, tooltip } = opts;

  const layer = Lmod.geoJSON(feature as any, {
    interactive: false,
    style: {
      color,
      weight: 2,
      opacity: 0.9,
      fillColor: color,
      fillOpacity: 0.06,
      // Dashed so it reads as a boundary the operator is working INSIDE, rather than as
      // a shape they drew.
      dashArray: '6 4',
    },
  }).addTo(map);

  const label = tooltip === false ? null : (tooltip || feature.properties?.udaName || null);
  if (label) layer.bindTooltip(label, { sticky: true, direction: 'top' });

  if (fit) {
    const bounds = layer.getBounds();
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20] });
  }

  return layer;
}
